/**
 * /market/:slug/cuenta — Cuenta del cliente B2C (Fase C).
 * Login / registro / perfil / direcciones / historial de pedidos.
 * Theme del tenant (light/dark auto). Mobile-first.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  User,
  MapPin,
  Package,
  LogOut,
  Plus,
  Trash2,
  Loader2,
  Sparkles,
  Download,
  Bell,
  Shield,
  FileDown,
  CalendarClock,
  GraduationCap,
} from "lucide-react";
import { useAppDialogs } from "@/components/ui/app-dialogs";
import { CustomerDownloadsSection } from "@/components/market/CustomerDownloadsSection";
import { StructuraPlatformSignature } from "@/components/StructuraPlatformSignature";
import { toast } from "sonner";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { getRequest } from "@tanstack/react-start/server";
import {
  getCustomerProfile,
  setCustomerSession,
  clearCustomerSession,
  customerFetch,
  getCustomerToken,
  type CustomerProfile,
} from "@/lib/market-customer-auth";
import { purgeLegacyAuthStorage, setAuthToken, setStoredTenantId } from "@/lib/sm-api";
import { shouldSmPersistBearerInClient } from "@/lib/sm-session-bridge";
import { CustomerPushOptIn } from "@/components/market/CustomerPushOptIn";
import { CustomerOrderList } from "@/components/market/CustomerOrderList";
import type { CustomerOrderHistoryRow } from "@/lib/customer-order-history";
import { CustomerReservationList } from "@/components/market/CustomerReservationList";
import type { CustomerReservationRow } from "@/lib/customer-reservation-history";
import { CustomerServiceBookingList } from "@/components/market/CustomerServiceBookingList";
import {
  CustomerClassEnrollmentList,
  type CustomerClassEnrollmentRow,
} from "@/components/market/CustomerClassEnrollmentList";
import type { CustomerServiceBookingRow } from "@/lib/customer-service-booking-history";
import { PageLoader } from "@/components/PageLoader";
import {
  EMPTY_NORMALIZED_ADDRESS,
  NormalizedAddressForm,
  validateAddressForm,
} from "@/components/address/NormalizedAddressForm";
import type { NormalizedAddress } from "@structura/core/address";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  .handler(
    // @ts-expect-error ServerFnCtx framework type mismatch
    async ({ data: slug }): Promise<MarketCatalogResult> =>
      loadMarketCatalog(slug, { request: getRequest() }),
  );

export const Route = createFileRoute("/market_/$slug/cuenta")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: AccountPage,
  head: ({ params }) => ({
    meta: [{ title: `Mi cuenta — ${params.slug}` }, { name: "robots", content: "noindex, nofollow" }],
    links: [
      { rel: "manifest", href: `/api/manifest/${params.slug}` },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap",
      },
    ],
  }),
});

type Address = {
  id: string;
  label: string | null;
  address: string;
  notes: string | null;
  isDefault: boolean;
  street?: string | null;
  city?: string | null;
  postalCode?: string | null;
};

function AccountPage() {
  const { slug } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;
  const data = result.ok ? result.data : null;

  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setProfile(getCustomerProfile(slug));
    setReady(true);
  }, [slug]);

  const offlineScreen = renderMarketPublicOffline(result);
  if (offlineScreen) return offlineScreen;

  if (!data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <Package className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Tienda no disponible</h1>
      </div>
    );
  }

  const { tenant } = data;
  const allowCustomerUi = data.customerAccountsEnabled;
  // Tokens de theme resueltos server-side vía Experience Engine (Fase D3,
  // mismo patrón validado en D1 — ver ADR-002). Antes se calculaba acá con
  // marketSurfaceStyle(); mismo resultado.
  const brandStyle = data.brand.surfaceStyle;
  const experienceBrand = data.experiencePublished?.brand;
  const tenantName = experienceBrand?.name?.trim() || tenant.name;
  const logoUrl = experienceBrand?.logo?.light?.src || tenant.logoUrl;
  // Sin B2C: igual mostramos login para el puente owner/staff → /app.
  const showCustomerProfile = allowCustomerUi && profile;

  return (
    <div
      className="market-surface flex min-h-[100dvh] flex-col"
      style={{
        ...brandStyle,
        backgroundImage:
          "radial-gradient(ellipse 90% 55% at 50% -15%, color-mix(in srgb, var(--market-accent) 16%, transparent), transparent 65%), linear-gradient(180deg, var(--market-bg), color-mix(in srgb, var(--market-accent) 5%, var(--market-bg)))",
        backgroundColor: "var(--market-bg)",
      }}
    >
      <header className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-3 border-b border-[var(--market-surface-variant)] bg-[var(--market-surface)]/90 px-4 backdrop-blur-md md:h-16 md:px-12">
        <Link
          to="/market/$slug"
          params={{ slug }}
          className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--market-on-surface-variant)] transition-colors hover:bg-[var(--market-surface-bright)]"
          aria-label="Volver a la tienda"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt=""
              className="h-7 w-7 shrink-0 rounded-lg object-contain bg-[var(--market-surface-bright)] p-0.5 ring-1 ring-[var(--market-surface-variant)]"
            />
          ) : null}
          <div className="min-w-0">
            <p className="market-font-display truncate text-base font-bold leading-tight text-[var(--market-accent)] md:text-lg">
              {tenantName}
            </p>
            <p className="truncate text-xs text-[var(--market-on-surface-variant)]">Mi cuenta</p>
          </div>
        </div>
      </header>

      {!ready ? (
        <div className="flex flex-1 items-center justify-center px-4 py-8">
          <PageLoader label="Cargando cuenta…" />
        </div>
      ) : showCustomerProfile ? (
        <div className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 md:px-6 md:py-8">
          <LoggedIn
            slug={slug}
            profile={profile!}
            tenantName={tenantName}
            showReservations={data.showReservations}
            showServiceBookings={data.showServiceBookings}
            onLogout={() => {
              clearCustomerSession(slug);
              setProfile(null);
            }}
            onProfile={setProfile}
          />
        </div>
      ) : (
        <main className="flex flex-1 items-center justify-center px-4 py-8">
          <AuthForms
            slug={slug}
            onAuthed={setProfile}
            allowRegister={allowCustomerUi}
            tenantName={tenantName}
            logoUrl={logoUrl}
          />
        </main>
      )}

      <footer className="shrink-0 border-t border-[var(--market-surface-variant)]/70 py-4">
        <StructuraPlatformSignature className="py-0" />
      </footer>
    </div>
  );
}

// ─── Login / Registro ────────────────────────────────────────────────────────

function AuthForms({
  slug,
  onAuthed,
  allowRegister = true,
  tenantName,
  logoUrl,
}: {
  slug: string;
  onAuthed: (p: CustomerProfile) => void;
  allowRegister?: boolean;
  tenantName: string;
  logoUrl: string | null;
}) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [saveAddress, setSaveAddress] = useState(true);
  const [address, setAddress] = useState<NormalizedAddress>({ ...EMPTY_NORMALIZED_ADDRESS });
  const [busy, setBusy] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);

  const effectiveMode = allowRegister ? mode : "login";

  const submitForgot = async () => {
    if (!forgotEmail.trim()) {
      toast.error("Ingresá tu email");
      return;
    }
    setForgotBusy(true);
    try {
      const res = await customerFetch(slug, `/api/market/${slug}/account?action=forgot`, {
        method: "POST",
        body: JSON.stringify({ email: forgotEmail }),
      });
      await res.json().catch(() => ({}));
      setForgotSent(true);
    } catch {
      toast.error("Error de red");
    } finally {
      setForgotBusy(false);
    }
  };

  const submit = async () => {
    if (effectiveMode === "register" && saveAddress) {
      const addrErr = validateAddressForm(address);
      if (addrErr) {
        toast.error(addrErr);
        return;
      }
    }
    setBusy(true);
    try {
      const res = await customerFetch(slug, `/api/market/${slug}/account?action=${effectiveMode}`, {
        method: "POST",
        body: JSON.stringify(form),
      });
      const d = (await res.json()) as {
        error?: string;
        kind?: string;
        redirectTo?: string;
        token?: string;
        customer?: CustomerProfile;
        user?: { tenantId?: string | null; name?: string };
      };
      if (!res.ok) {
        toast.error(d.error ?? "Error");
        return;
      }

      // Owner/staff del tenant → sesión sm_session y /app en el mismo host.
      const isStaffBridge =
        d.kind === "staff" || (typeof d.redirectTo === "string" && d.redirectTo.startsWith("/app"));
      if (isStaffBridge) {
        if (d.token && shouldSmPersistBearerInClient()) setAuthToken(d.token);
        else purgeLegacyAuthStorage();
        if (d.user?.tenantId) setStoredTenantId(d.user.tenantId);
        window.location.assign(d.redirectTo ?? "/app/dashboard");
        return;
      }

      if (!d.token || !d.customer) {
        toast.error("Respuesta inválida");
        return;
      }
      setCustomerSession(slug, d.token, d.customer);
      if (effectiveMode === "register" && saveAddress) {
        await customerFetch(slug, `/api/market/${slug}/account/addresses`, {
          method: "POST",
          body: JSON.stringify({ ...address, isDefault: true }),
        }).catch(() => {});
      }
      onAuthed(d.customer);
      toast.success(effectiveMode === "register" ? "Cuenta creada" : `Hola, ${d.customer.name}`);
    } catch {
      toast.error("Error de red");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="animate-fade-up w-full max-w-md rounded-2xl border border-[var(--market-outline)] bg-[var(--market-surface-bright)] p-6 shadow-sm md:p-8"
      style={{ animationDuration: "0.45s" }}
    >
      <div className="mb-6 flex flex-col items-center text-center">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="mb-3 h-14 w-14 rounded-2xl object-contain bg-[var(--market-surface)] p-1.5 ring-1 ring-[var(--market-surface-variant)]"
          />
        ) : (
          <div
            className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--market-accent)]/15 text-xl font-extrabold text-[var(--market-accent)]"
            aria-hidden
          >
            {tenantName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <h1 className="market-font-display text-2xl font-extrabold leading-tight text-[var(--market-on-surface)]">
          {tenantName}
        </h1>
        <p className="mt-1.5 text-sm text-[var(--market-on-surface-variant)]">
          {forgotMode ? "Recuperar contraseña" : `Ingresá a tu cuenta en ${tenantName}`}
        </p>
      </div>

      {forgotMode ? (
        <div
          className="space-y-3.5 opacity-0 animate-fade-up"
          style={{ animationDelay: "80ms", animationFillMode: "forwards", animationDuration: "0.4s" }}
        >
          {forgotSent ? (
            <p className="rounded-xl border border-[var(--market-outline)] bg-[var(--market-surface)] p-4 text-sm text-[var(--market-on-surface)]">
              Si el email está registrado en {tenantName}, vas a recibir instrucciones para crear una
              contraseña nueva.
            </p>
          ) : (
            <>
              <p className="text-sm text-[var(--market-on-surface-variant)]">
                Ingresá el email de tu cuenta y te enviamos un enlace para crear una contraseña nueva.
              </p>
              <Field label="Email" type="email" value={forgotEmail} onChange={setForgotEmail} />
              <button
                type="button"
                disabled={forgotBusy}
                onClick={submitForgot}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--market-accent)] py-3.5 text-base font-bold text-[var(--primary-foreground)] transition-transform active:scale-[0.98] disabled:opacity-50"
              >
                {forgotBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                Enviar instrucciones
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => {
              setForgotMode(false);
              setForgotSent(false);
              setForgotEmail("");
            }}
            className="w-full text-center text-sm font-medium text-[var(--market-accent)] hover:underline"
          >
            Volver al login
          </button>
        </div>
      ) : (
        <>
          {allowRegister ? (
            <div
              className="mb-5 flex gap-1 rounded-full border border-[var(--market-surface-variant)] bg-[var(--market-surface)] p-1 opacity-0 animate-fade-up"
              style={{ animationDelay: "80ms", animationFillMode: "forwards", animationDuration: "0.4s" }}
            >
              {(["login", "register"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors ${
                    mode === m
                      ? "bg-[var(--market-accent)] text-[var(--primary-foreground)] shadow-sm"
                      : "text-[var(--market-on-surface-variant)] hover:text-[var(--market-on-surface)]"
                  }`}
                >
                  {m === "login" ? "Ingresar" : "Crear cuenta"}
                </button>
              ))}
            </div>
          ) : (
            <p className="mb-5 text-center text-sm text-[var(--market-on-surface-variant)]">
              Ingresá con tu cuenta de comercio para abrir el panel.
            </p>
          )}

          <div
            className="space-y-3.5 opacity-0 animate-fade-up"
            style={{ animationDelay: "140ms", animationFillMode: "forwards", animationDuration: "0.4s" }}
          >
            {effectiveMode === "register" && (
              <Field label="Nombre" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
            )}
            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={(v) => setForm((f) => ({ ...f, email: v }))}
            />
            <Field
              label="Contraseña"
              type="password"
              value={form.password}
              onChange={(v) => setForm((f) => ({ ...f, password: v }))}
            />
            {effectiveMode === "login" && (
              <button
                type="button"
                onClick={() => setForgotMode(true)}
                className="-mt-1 block text-right text-xs font-medium text-[var(--market-accent)] hover:underline"
              >
                ¿Olvidaste tu contraseña?
              </button>
            )}
            {effectiveMode === "register" && (
              <Field
                label="Teléfono (opcional)"
                value={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              />
            )}
            {effectiveMode === "register" && (
              <div className="rounded-xl border border-[var(--market-outline)] bg-[var(--market-surface)] p-3">
                <label className="mb-2 flex items-center gap-2 text-xs font-medium text-[var(--market-on-surface)]">
                  <input
                    type="checkbox"
                    checked={saveAddress}
                    onChange={(e) => setSaveAddress(e.target.checked)}
                    className="accent-[var(--market-accent)]"
                  />
                  Guardar mi domicilio principal
                </label>
                {saveAddress && (
                  <NormalizedAddressForm
                    value={address}
                    onChange={setAddress}
                    variant="market"
                    showLabel={false}
                  />
                )}
              </div>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={submit}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--market-accent)] py-3.5 text-base font-bold text-[var(--primary-foreground)] transition-transform active:scale-[0.98] disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {effectiveMode === "login" ? "Ingresar" : "Crear cuenta"}
            </button>
          </div>

          {allowRegister ? (
            <p className="mt-5 text-center text-xs leading-relaxed text-[var(--market-on-surface-variant)]">
              ¿Sos el dueño o el equipo? Entrá con tu usuario del panel.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

// ─── Logueado: perfil + direcciones + pedidos ───────────────────────────────────

function LoggedIn({
  slug,
  profile,
  onLogout,
  onProfile,
  tenantName,
  showReservations,
  showServiceBookings,
}: {
  slug: string;
  profile: CustomerProfile;
  onLogout: () => void;
  onProfile: (p: CustomerProfile) => void;
  tenantName: string;
  showReservations: boolean;
  showServiceBookings: boolean;
}) {
  const [orders, setOrders] = useState<CustomerOrderHistoryRow[]>([]);
  const [reservations, setReservations] = useState<CustomerReservationRow[]>([]);
  const [serviceBookings, setServiceBookings] = useState<CustomerServiceBookingRow[]>([]);
  const [classEnrollments, setClassEnrollments] = useState<CustomerClassEnrollmentRow[]>([]);
  const [clientResources, setClientResources] = useState<
    Array<{ id: string; title: string; fileUrl: string; description: string | null }>
  >([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [newAddr, setNewAddr] = useState<NormalizedAddress>({ ...EMPTY_NORMALIZED_ADDRESS });
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [loyalty, setLoyalty] = useState<{ enabled: boolean; balance: number } | null>(null);
  const [digitalEnabled, setDigitalEnabled] = useState(true);
  const [emailOptout, setEmailOptout] = useState<boolean>(profile.marketingEmailOptout ?? false);
  const [emailBusy, setEmailBusy] = useState(false);

  useEffect(() => {
    void customerFetch(slug, `/api/market/${slug}/account/orders`)
      .then((r) => r.json())
      .then((d) => setOrders(d.orders ?? []))
      .catch(() => {});
    if (showReservations) {
      void customerFetch(slug, `/api/market/${slug}/account/reservations`)
        .then((r) => r.json())
        .then((d) => setReservations(d.reservations ?? []))
        .catch(() => {});
    }
    if (showServiceBookings) {
      void customerFetch(slug, `/api/market/${slug}/account/service-bookings`)
        .then((r) => r.json())
        .then((d) => setServiceBookings(d.bookings ?? []))
        .catch(() => {});
      void customerFetch(slug, `/api/market/${slug}/account/resources`)
        .then((r) => r.json())
        .then((d) => setClientResources(d.resources ?? []))
        .catch(() => {});
      void customerFetch(slug, `/api/market/${slug}/account/class-enrollments`)
        .then((r) => r.json())
        .then((d) => setClassEnrollments(d.enrollments ?? []))
        .catch(() => {});
    }
    void customerFetch(slug, `/api/market/${slug}/account/addresses`)
      .then((r) => r.json())
      .then((d) => setAddresses(d.addresses ?? []))
      .catch(() => {});
    void customerFetch(slug, `/api/market/${slug}/account/loyalty`)
      .then((r) => r.json())
      .then((d) => setLoyalty({ enabled: !!d.enabled, balance: d.balance ?? 0 }))
      .catch(() => {});
    void customerFetch(slug, `/api/market/${slug}/account`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.customer) {
          const token = getCustomerToken(slug);
          if (token) setCustomerSession(slug, token, d.customer);
          onProfile(d.customer);
          setEmailOptout(Boolean(d.customer.marketingEmailOptout));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh once per slug
  }, [slug]);

  const addAddress = async () => {
    const err = validateAddressForm(newAddr);
    if (err) {
      toast.error(err);
      return;
    }
    const res = await customerFetch(slug, `/api/market/${slug}/account/addresses`, {
      method: "POST",
      body: JSON.stringify({ ...newAddr, isDefault: addresses.length === 0 }),
    });
    const d = await res.json();
    if (res.ok) {
      setAddresses(d.addresses);
      setNewAddr({ ...EMPTY_NORMALIZED_ADDRESS });
      setShowAddrForm(false);
    } else toast.error(d.error ?? "Error");
  };
  const delAddress = async (id: string) => {
    const res = await customerFetch(slug, `/api/market/${slug}/account/addresses?id=${id}`, {
      method: "DELETE",
    });
    if (res.ok) setAddresses((a) => a.filter((x) => x.id !== id));
  };

  const toggleEmailOptout = async (wantsOptout: boolean) => {
    const token = profile.unsubscribeToken;
    if (!token) {
      toast.error("No disponible");
      return;
    }
    setEmailBusy(true);
    try {
      const res = await fetch(`/api/market/${slug}/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, resubscribe: !wantsOptout }),
      });
      if (res.ok) {
        setEmailOptout(wantsOptout);
        toast.success(wantsOptout ? "Desuscripto de emails de marketing" : "Suscripto a emails de marketing");
      }
    } catch {
      toast.error("Error al guardar preferencia");
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div className="animate-fade-up space-y-6 md:grid md:grid-cols-5 md:items-start md:gap-6 md:space-y-0">
      <div className="space-y-5 md:col-span-2">
        {/* Perfil */}
        <section className="rounded-2xl border border-[var(--market-outline)] bg-[var(--market-surface-bright)] p-5">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--market-accent)]/15 text-[var(--market-accent)]">
              <User className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="market-font-display text-lg font-bold leading-tight text-[var(--market-on-surface)]">
                {profile.name}
              </p>
              <p className="mt-0.5 break-all text-sm text-[var(--market-on-surface-variant)]">
                {profile.email}
              </p>
              {profile.phone ? (
                <p className="mt-0.5 text-sm text-[var(--market-on-surface-variant)]">{profile.phone}</p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-[var(--market-surface-variant)] py-2.5 text-sm font-medium text-[var(--market-on-surface-variant)] hover:border-red-500/40 hover:text-red-500"
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </section>

        {/* Opt-in de push de pedidos */}
        <CustomerPushOptIn customerId={profile.id} />

        {/* Puntos (loyalty) */}
        {loyalty?.enabled && (
          <section className="flex items-center gap-4 rounded-2xl border border-[var(--market-accent)]/30 bg-[var(--market-accent)]/10 p-5">
            <Sparkles className="h-6 w-6 shrink-0 text-[var(--market-accent)]" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-[var(--market-on-surface-variant)]">Tus puntos</p>
              <p className="market-font-display text-2xl font-bold text-[var(--market-accent)]">
                {loyalty.balance}
              </p>
            </div>
            <p className="text-xs text-[var(--market-on-surface-variant)]">Canjealos en tu próximo pedido.</p>
          </section>
        )}

        {/* Referral — compacto, solo si existe */}
        {profile.referralCode ? (
          <section
            className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] p-5"
            data-testid="customer-referral-share"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--market-on-surface-variant)]">
              Referidos
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate rounded-lg bg-[var(--market-surface)] px-3 py-2 text-sm font-mono text-[var(--market-on-surface)]">
                {profile.referralCode}
              </code>
              <button
                type="button"
                className="shrink-0 rounded-full bg-[var(--market-accent)]/10 px-3 py-2 text-xs font-semibold text-[var(--market-accent)] hover:bg-[var(--market-accent)]/20"
                onClick={() => {
                  const url = `${window.location.origin}/market/${slug}?ref=${encodeURIComponent(profile.referralCode!)}`;
                  void navigator.clipboard.writeText(url).then(() => toast.success("Link copiado"));
                }}
              >
                Copiar
              </button>
            </div>
          </section>
        ) : null}

        {/* Preferencias de email */}
        <section className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] p-5">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--market-on-surface)]">
            <Bell className="h-4 w-4 text-[var(--market-accent)]" /> Notificaciones por email
          </h2>
          {profile.unsubscribeToken ? (
            <label className="flex cursor-pointer items-start gap-3">
              <div className="relative mt-0.5 shrink-0">
                <input
                  type="checkbox"
                  checked={!emailOptout}
                  disabled={emailBusy}
                  onChange={(e) => {
                    void toggleEmailOptout(!e.target.checked);
                  }}
                  className="sr-only"
                />
                <div
                  className={`h-5 w-9 rounded-full transition-colors ${!emailOptout ? "bg-[var(--market-accent)]" : "bg-[var(--market-surface-variant)]"}`}
                >
                  <div
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${!emailOptout ? "translate-x-4" : "translate-x-0.5"}`}
                  />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-[var(--market-on-surface)]">
                  Emails de marketing y novedades
                </p>
                <p className="text-xs text-[var(--market-on-surface-variant)]">
                  {emailOptout ? "No estás suscripto" : "Estás suscripto"}
                </p>
              </div>
            </label>
          ) : (
            <p className="text-sm text-[var(--market-on-surface-variant)]">
              No disponible — comprá algo primero para habilitar esta opción.
            </p>
          )}
        </section>

        {/* Privacidad (Ley 25.326 / GDPR) — exportar o eliminar mis datos */}
        <PrivacyControls slug={slug} email={profile.email} />

        {/* Direcciones */}
        <section className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] p-5">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-[var(--market-on-surface)]">
            <MapPin className="h-4 w-4 text-[var(--market-accent)]" /> Mis direcciones
          </h2>
          <div className="space-y-3">
            {addresses.map((a) => (
              <div
                key={a.id}
                className="flex items-start gap-3 rounded-xl border border-[var(--market-surface-variant)] p-4"
              >
                <div className="min-w-0 flex-1">
                  {a.isDefault && (
                    <span className="mb-1.5 inline-block rounded-full bg-[var(--market-accent)]/15 px-2 py-0.5 text-xs font-semibold text-[var(--market-accent)]">
                      Principal
                    </span>
                  )}
                  <p className="text-sm font-medium leading-snug text-[var(--market-on-surface)]">
                    {a.address}
                  </p>
                  {a.city && (
                    <p className="mt-0.5 text-xs text-[var(--market-on-surface-variant)]">
                      {[a.postalCode, a.city].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => delAddress(a.id)}
                  className="shrink-0 rounded-full p-1.5 text-[var(--market-on-surface-variant)] hover:bg-red-500/10 hover:text-red-500"
                  aria-label="Eliminar dirección"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {addresses.length === 0 && (
              <p className="text-sm text-[var(--market-on-surface-variant)]">
                Todavía no guardaste direcciones.
              </p>
            )}
          </div>
          {!showAddrForm ? (
            <button
              type="button"
              onClick={() => setShowAddrForm(true)}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-dashed border-[var(--market-surface-variant)] py-3 text-sm font-medium text-[var(--market-accent)]"
            >
              <Plus className="h-4 w-4" /> Agregar domicilio
            </button>
          ) : (
            <div className="mt-4 space-y-4 rounded-xl border border-[var(--market-surface-variant)] p-4">
              <NormalizedAddressForm value={newAddr} onChange={setNewAddr} variant="market" />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddrForm(false);
                    setNewAddr({ ...EMPTY_NORMALIZED_ADDRESS });
                  }}
                  className="flex-1 rounded-full border border-[var(--market-surface-variant)] py-2.5 text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={addAddress}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[var(--market-accent)] py-2.5 text-sm font-semibold text-[var(--primary-foreground)]"
                >
                  <Plus className="h-4 w-4" /> Guardar
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      <div className="space-y-5 md:col-span-3">
        {/* Descargas digitales */}
        {digitalEnabled && (
          <section className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] p-5">
            <h2 className="mb-4 flex items-center gap-2 market-font-display text-base font-bold text-[var(--market-on-surface)]">
              <Download className="h-4 w-4 text-[var(--market-accent)]" /> Mis descargas
            </h2>
            <CustomerDownloadsSection slug={slug} onEnabledChange={setDigitalEnabled} />
          </section>
        )}

        {showServiceBookings && (
          <section className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] p-5">
            <h2 className="mb-4 flex items-center gap-2 market-font-display text-base font-bold text-[var(--market-on-surface)]">
              <CalendarClock className="h-4 w-4 text-[var(--market-accent)]" /> Mis turnos
            </h2>
            <CustomerServiceBookingList slug={slug} bookings={serviceBookings} />
          </section>
        )}

        {showServiceBookings && classEnrollments.length > 0 && (
          <section className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] p-5">
            <h2 className="mb-4 flex items-center gap-2 market-font-display text-base font-bold text-[var(--market-on-surface)]">
              <GraduationCap className="h-4 w-4 text-[var(--market-accent)]" /> Mis clases
            </h2>
            <CustomerClassEnrollmentList slug={slug} enrollments={classEnrollments} />
          </section>
        )}

        {showServiceBookings && clientResources.length > 0 && (
          <section className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] p-5">
            <h2 className="mb-4 flex items-center gap-2 market-font-display text-base font-bold text-[var(--market-on-surface)]">
              <Download className="h-4 w-4 text-[var(--market-accent)]" /> Mis recursos
            </h2>
            <ul className="divide-y">
              {clientResources.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                  <div>
                    <p className="font-medium">{r.title}</p>
                    {r.description ? (
                      <p className="text-xs text-[var(--market-on-surface-variant)]">{r.description}</p>
                    ) : null}
                  </div>
                  <a href={r.fileUrl} className="underline" target="_blank" rel="noreferrer">
                    Descargar
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Reservas de mesa (SCRUM-259) */}
        {showReservations && (
          <section className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] p-5">
            <h2 className="mb-4 flex items-center gap-2 market-font-display text-base font-bold text-[var(--market-on-surface)]">
              <CalendarClock className="h-4 w-4 text-[var(--market-accent)]" /> Mis turnos
            </h2>
            <CustomerReservationList slug={slug} reservations={reservations} />
          </section>
        )}

        {/* Pedidos */}
        <section className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] p-5">
          <h2 className="mb-4 flex items-center gap-2 market-font-display text-base font-bold text-[var(--market-on-surface)]">
            <Package className="h-4 w-4 text-[var(--market-accent)]" /> Mis pedidos
          </h2>
          <CustomerOrderList slug={slug} orders={orders} />
        </section>
      </div>
    </div>
  );
}

// ─── Privacidad (SCRUM-208) ──────────────────────────────────────────────────

function PrivacyControls({ slug, email }: { slug: string; email: string }) {
  const { confirm, alert } = useAppDialogs();
  const [busy, setBusy] = useState<"export" | "delete" | null>(null);

  const submit = async (type: "EXPORT" | "DELETE") => {
    setBusy(type === "EXPORT" ? "export" : "delete");
    try {
      const res = await customerFetch(slug, `/api/market/${slug}/privacy-request`, {
        method: "POST",
        body: JSON.stringify({ type, email }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        referenceCode?: string;
      };
      if (!res.ok || !data.ok) {
        toast.error(data.error ?? "No se pudo crear la solicitud");
        return;
      }
      await alert({
        title: "Solicitud registrada",
        description:
          `Guardá este código de seguimiento: ${data.referenceCode}\n\n` +
          (type === "EXPORT"
            ? "Recibirás por email un aviso cuando el archivo esté listo. Podés consultar el estado desde el enlace público con tu código."
            : "El comercio revisará la solicitud y procederá a anonimizar tus datos. La ley obliga a conservar comprobantes fiscales aún después de la eliminación."),
      });
    } catch {
      toast.error("Error de red al enviar la solicitud");
    } finally {
      setBusy(null);
    }
  };

  const onExport = async () => {
    const ok = await confirm({
      title: "Solicitar exportación de datos",
      description:
        "Vamos a registrar una solicitud para que el comercio te entregue una copia de tus datos personales (perfil, direcciones, pedidos). " +
        "Es un derecho garantizado por la Ley 25.326 (AR).",
      confirmLabel: "Solicitar exportación",
    });
    if (ok) await submit("EXPORT");
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: "Solicitar eliminación de mis datos",
      description:
        "Se registrará tu solicitud de eliminación. El comercio anonimizará tu nombre, email y teléfono, y borrará tus direcciones. " +
        "Las órdenes históricas se conservan por obligación fiscal (AFIP) pero quedarán sin datos personales.",
      destructive: true,
      confirmLabel: "Sí, solicitar eliminación",
    });
    if (ok) await submit("DELETE");
  };

  return (
    <section className="rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] p-5">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--market-on-surface)]">
        <Shield className="h-4 w-4 text-[var(--market-accent)]" /> Mis datos personales
      </h2>
      <p className="mb-3 text-xs text-[var(--market-on-surface-variant)]">
        Podés solicitar una copia de tus datos o pedir que sean eliminados (Ley 25.326).
      </p>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onExport}
          disabled={busy !== null}
          className="flex w-full items-center justify-center gap-2 rounded-full border border-[var(--market-surface-variant)] py-2.5 text-sm font-medium text-[var(--market-on-surface)] transition-colors hover:border-[var(--market-accent)] hover:text-[var(--market-accent)] disabled:opacity-50"
        >
          {busy === "export" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileDown className="h-4 w-4" />
          )}
          Exportar mis datos
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={busy !== null}
          className="flex w-full items-center justify-center gap-2 rounded-full border border-[var(--market-surface-variant)] py-2.5 text-sm font-medium text-[var(--market-on-surface-variant)] transition-colors hover:border-red-500/50 hover:text-red-500 disabled:opacity-50"
        >
          {busy === "delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Solicitar eliminación de mis datos
        </button>
      </div>
    </section>
  );
}

export function Field({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-[var(--market-on-surface-variant)]">
        {label}
      </span>
      {/* Sin clase market-input: ese helper CSS fuerza border:none (look borderless). */}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-xl border border-[var(--market-outline)] bg-[var(--market-surface)] px-4 text-base text-[var(--market-on-surface)] outline-none transition-[border-color,box-shadow] placeholder:text-[var(--market-on-surface-variant)]/70 focus:border-[var(--market-accent)] focus:ring-2 focus:ring-[var(--market-accent)]/25"
      />
    </label>
  );
}
